import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      const dest = user.role === "superadmin" ? "/superadmin" : "/dashboard";
      nav(dest, { replace: true });
    }
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(t("common.session_started"));
      nav(u.role === "superadmin" ? "/superadmin" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenciales inválidas");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Top bar */}
      <header className="border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-1" data-testid="brand-home">
            <span className="text-2xl font-semibold tracking-tighter">vincularedu</span>
            <span className="text-2xl font-medium text-zinc-400">.</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link
              to="/"
              className="text-sm text-zinc-500 hover:text-zinc-950 inline-flex items-center gap-1.5"
              data-testid="back-home"
            >
              <ArrowLeft size={14} strokeWidth={1.5} /> {t("nav.volver_inicio")}
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-zinc-50">
        <div className="w-full max-w-md bg-white border border-zinc-200 p-8 sm:p-10">
          <div className="section-marker mb-4">{t("login.kicker")}</div>
          <h1 className="text-3xl sm:text-4xl font-medium tracking-tight mb-2">{t("login.title")}</h1>
          <p className="text-sm text-zinc-500 mb-8">
            {t("login.no_account")}{" "}
            <Link to="/demo" className="underline text-zinc-950">{t("login.try_demo")}</Link>.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                {t("login.email")}
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-zinc-300 bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-zinc-950 text-base"
                data-testid="login-email"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                {t("login.password")}
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-zinc-300 bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-zinc-950 text-base"
                data-testid="login-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-zinc-950 text-white px-6 py-4 font-medium hover:bg-zinc-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="login-submit"
            >
              {loading ? t("login.loading") : t("login.submit")}
              <ArrowRight size={18} strokeWidth={1.5} />
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-zinc-200 text-sm text-zinc-500 text-center">
            {t("login.contact_text")} <br />
            <a href="mailto:contacto@cdesign.cl" className="underline text-zinc-950" data-testid="contact-link">
              contacto@cdesign.cl
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
